import { type WidgetData, type WidgetSizeDefinition } from "@repo/api";
import { logError } from "@repo/utils";

const matrixWidth = 4;
// max lines of the grid
const matrixHeight = 1000;

function isOccupied(
  left: number,
  top: number,
  col: number,
  row: number,
  ignoreIndex: number,
  widgets: WidgetData[]
): boolean {
  return widgets.some((w, i) => {
    if (i === ignoreIndex) {
      return false;
    }
    return (
      left < w.widgetSettings.left + w.widgetSettings.appSize.col &&
      left + col > w.widgetSettings.left &&
      top < w.widgetSettings.top + w.widgetSettings.appSize.row &&
      top + row > w.widgetSettings.top
    );
  });
}

function findNextPosition(
  widget: WidgetData,
  widgets: WidgetData[]
): { left: number; top: number } {
  for (let top = 0; top < matrixHeight; top++) {
    for (
      let left = 0;
      left <= matrixWidth - widget.widgetSettings.appSize.col;
      left++
    ) {
      let canPlace = true;
      for (let w = 0; w < widget.widgetSettings.appSize.col; w++) {
        for (let h = 0; h < widget.widgetSettings.appSize.row; h++) {
          if (
            isOccupied(
              left + w,
              top + h,
              widget.widgetSettings.appSize.col,
              widget.widgetSettings.appSize.row,
              widgets.indexOf(widget),
              widgets
            )
          ) {
            canPlace = false;
            break;
          }
        }
        if (!canPlace) {
          break;
        }
      }
      if (canPlace) {
        return { left, top };
      }
    }
  }
  logError("No available position found.");
  throw new Error("No available position found.");
}

function repositionWidgets(
  widgets: WidgetData[],
  widgetIndex: number,
  newLeft: number,
  newTop: number
) {
  const newWidgets = [...widgets];
  newWidgets[widgetIndex].widgetSettings.left = newLeft;
  newWidgets[widgetIndex].widgetSettings.top = newTop;
  const updatedWidgets = [widgets[widgetIndex]];

  for (let i = 0; i < newWidgets.length; i++) {
    if (i === widgetIndex) {
      continue;
    }
    const otherWidget = newWidgets[i];
    if (
      isOccupied(
        otherWidget.widgetSettings.left,
        otherWidget.widgetSettings.top,
        otherWidget.widgetSettings.appSize.col,
        otherWidget.widgetSettings.appSize.row,
        i,
        newWidgets
      )
    ) {
      const { left, top } = findNextPosition(otherWidget, newWidgets);
      newWidgets[i].widgetSettings.left = left;
      newWidgets[i].widgetSettings.top = top;
      updatedWidgets.push(newWidgets[i]);
    }
  }

  return { newWidgets, updatedWidgets };
}

/**
 * Remove empty row gaps by shifting widgets up so there are no
 * fully unoccupied rows between occupied rows.
 */
function compactRows(
  widgets: WidgetData[],
  alreadyUpdated: WidgetData[]
): { compactedWidgets: WidgetData[]; updatedWidgets: WidgetData[] } {
  if (widgets.length === 0) {
    return { compactedWidgets: widgets, updatedWidgets: alreadyUpdated };
  }

  const maxRow = widgets.reduce(
    (max, w) =>
      Math.max(max, w.widgetSettings.top + w.widgetSettings.appSize.row),
    0
  );

  // Track which rows are occupied
  const occupiedRows = new Set<number>();
  for (const w of widgets) {
    for (
      let r = w.widgetSettings.top;
      r < w.widgetSettings.top + w.widgetSettings.appSize.row;
      r++
    ) {
      occupiedRows.add(r);
    }
  }

  // Build a mapping from old row → new row by removing empty row gaps
  const rowMapping = new Map<number, number>();
  let newRow = 0;
  for (let r = 0; r < maxRow; r++) {
    if (occupiedRows.has(r)) {
      rowMapping.set(r, newRow);
      newRow++;
    }
  }

  const updatedWidgets = [...alreadyUpdated];

  for (const w of widgets) {
    const mappedTop = rowMapping.get(w.widgetSettings.top);
    if (mappedTop !== undefined && mappedTop !== w.widgetSettings.top) {
      w.widgetSettings.top = mappedTop;
      if (!updatedWidgets.includes(w)) {
        updatedWidgets.push(w);
      }
    }
  }

  return { compactedWidgets: widgets, updatedWidgets };
}

export function moveWidget(
  widgets: WidgetData[],
  widgetId: number,
  direction: "LEFT" | "RIGHT" | "UP" | "DOWN"
) {
  const widgetIndex = widgets.findIndex(
    (widget) => widget.workspacePreferenceId === widgetId
  );
  if (widgetIndex === -1) {
    throw new Error(`Widget with ID ${widgetId} not found.`);
  }

  const widget = widgets[widgetIndex];
  const {
    left,
    top,
    appSize: { col },
  } = widget.widgetSettings;

  // Calculate the new position based on the direction
  let newLeft = left;
  let newTop = top;

  // When in Mobile wview, UP and DOWN should be "left" and "right"
  const d =
    window.innerWidth < 900 // SKAPA L
      ? direction === "LEFT" || direction === "UP"
        ? "LEFT"
        : "RIGHT"
      : direction;
  switch (d) {
    case "LEFT":
      newLeft = Math.max(0, left - 1);
      if (left - 1 < 0 && top > 0) {
        newTop -= 1;
        newLeft = matrixWidth - col;
      }
      break;
    case "RIGHT":
      newLeft = Math.min(matrixWidth - col, left + 1);
      if (left + 1 >= matrixWidth) {
        newTop += 1;
        newLeft = 0;
      }
      break;
    case "UP":
      newTop = Math.max(0, top - 1);
      break;
    case "DOWN":
      newTop = top + 1;
      break;
  }

  const { newWidgets, updatedWidgets } = repositionWidgets(
    widgets,
    widgetIndex,
    newLeft,
    newTop
  );

  const { compactedWidgets, updatedWidgets: finalUpdated } = compactRows(
    newWidgets,
    updatedWidgets
  );

  return { newWidgets: compactedWidgets, updatedWidgets: finalUpdated };
}
export function getNewCoordinates(
  widgets: WidgetData[],
  newWidget: WidgetSizeDefinition
): { left: number; top: number } {
  if (newWidget.col > matrixWidth) {
    throw new Error("Widget width exceeds matrix width.");
  }

  const tempWidget: WidgetData = {
    workspacePreferenceId: -1,
    widgetType: "EMPTY",
    widgetSettings: {
      left: 0,
      top: 0,
      appSize: newWidget,
      appSetting: {},
    },
  };

  const { left, top } = findNextPosition(tempWidget, widgets);
  return { left, top };
}

export function moveWidgetToPosition(
  widgets: WidgetData[],
  widgetId: number,
  newLeft: number,
  newTop: number
) {
  const widgetIndex = widgets.findIndex(
    (widget) => widget.workspacePreferenceId === widgetId
  );
  if (widgetIndex === -1) {
    throw new Error(`Widget with ID ${widgetId} not found.`);
  }

  const widget = widgets[widgetIndex];
  const { col } = widget.widgetSettings.appSize;

  const clampedLeft = Math.max(0, Math.min(matrixWidth - col, newLeft));
  const clampedTop = Math.max(0, newTop);

  const { newWidgets, updatedWidgets } = repositionWidgets(
    widgets,
    widgetIndex,
    clampedLeft,
    clampedTop
  );

  const { compactedWidgets, updatedWidgets: finalUpdated } = compactRows(
    newWidgets,
    updatedWidgets
  );

  return { newWidgets: compactedWidgets, updatedWidgets: finalUpdated };
}

export function resizeWidget(
  widgets: WidgetData[],
  widgetId: number,
  size: WidgetSizeDefinition
): WidgetData[] {
  const widgetIndex = widgets.findIndex(
    (widget) => widget.workspacePreferenceId === widgetId
  );
  if (widgetIndex === -1) {
    throw new Error(`Widget with ID ${widgetId} not found.`);
  }

  const widget = widgets[widgetIndex];
  const { left, top } = widget.widgetSettings;

  // Update the widget size
  widget.widgetSettings.appSize.col = size.col;
  widget.widgetSettings.appSize.row = size.row;
  widget.widgetSettings.appSize.size = size.size;

  // Create a new array to avoid modifying the original array
  const newWidgets = widgets.slice();
  const updatedWidgets = [widget];
  // Handle conflicts with other widgets
  for (let i = 0; i < newWidgets.length; i++) {
    if (i === widgetIndex) {
      continue;
    }
    const otherWidget = newWidgets[i];

    // If there is a collision, try to move the conflicting widget to the right or left
    if (isOccupied(left, top, size.col, size.row, widgetIndex, newWidgets)) {
      let moveLeft = true;
      let otherLeft = otherWidget.widgetSettings.left;
      let otherTop = otherWidget.widgetSettings.top;

      while (
        isOccupied(
          otherLeft,
          otherTop,
          otherWidget.widgetSettings.appSize.col,
          otherWidget.widgetSettings.appSize.row,
          i,
          newWidgets
        )
      ) {
        if (moveLeft) {
          otherLeft--;
          if (otherLeft < 0) {
            moveLeft = false;
            otherLeft = otherWidget.widgetSettings.left + 1;
          }
        } else {
          otherLeft++;
          if (
            otherLeft + otherWidget.widgetSettings.appSize.col >
            matrixWidth
          ) {
            otherLeft = 0;
            otherTop++;
          }
        }
      }

      newWidgets[i].widgetSettings.left = otherLeft;
      newWidgets[i].widgetSettings.top = otherTop;
      updatedWidgets.push(newWidgets[i]);
    }
  }

  return updatedWidgets;
}
